import api from './api';

/**
 * Application Service - API client for application management
 */
const applicationService = {
  /**
   * Create a new application
   * @param {Object} data - Application data
   * @param {string} data.candidateId - Candidate ID
   * @param {string} data.jobPosting - Job posting title
   * @returns {Promise<Object>} Created application
   */
  createApplication: async (data) => {
    const response = await api.post('/legacy-applications', data);
    return response.data;
  },

  /**
   * Health check endpoint
   * @returns {Promise<Object>} Health status
   */
  healthCheck: async () => {
    const response = await api.get('/legacy-applications/health');
    return response.data;
  },

  /**
   * Get all applications with filtering
   * @param {Object} filters - Filter options
   * @param {string} filters.candidateId - Filter by candidate
   * @param {string} filters.currentStage - Filter by stage
   * @param {string} filters.status - Filter by status (alias for currentStage)
   * @param {string} filters.destinationCountry - Filter by destination country
   * @param {string} filters.jobPosting - Filter by job position
   * @param {string} filters.paymentStatus - Filter by payment status
   * @param {string} filters.gender - Filter by candidate gender
   * @param {string} filters.currentLocation - Filter by candidate location
   * @param {number} filters.minWorkExperience - Filter by minimum work experience
   * @param {number} filters.maxWorkExperience - Filter by maximum work experience
   * @param {string} filters.duration - Filter by application date duration
   * @param {number} filters.page - Page number
   * @param {number} filters.limit - Items per page
   * @returns {Promise<Object>} Applications list with pagination
   */
  getApplications: async (filters = {}) => {
    const params = new URLSearchParams();

    // Only add parameters that have values
    if (filters.candidateId) params.append('candidateId', filters.candidateId);
    if (filters.currentStage) params.append('currentStage', filters.currentStage);
    if (filters.status) params.append('status', filters.status);
    if (filters.destinationCountry) params.append('destinationCountry', filters.destinationCountry);
    if (filters.jobPosting) params.append('jobPosting', filters.jobPosting);
    if (filters.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
    if (filters.gender) params.append('gender', filters.gender);
    if (filters.currentLocation) params.append('currentLocation', filters.currentLocation);
    if (filters.minWorkExperience !== undefined && filters.minWorkExperience !== null && filters.minWorkExperience !== '') {
      params.append('minWorkExperience', filters.minWorkExperience.toString());
    }
    if (filters.maxWorkExperience !== undefined && filters.maxWorkExperience !== null && filters.maxWorkExperience !== '') {
      params.append('maxWorkExperience', filters.maxWorkExperience.toString());
    }
    if (filters.duration) params.append('duration', filters.duration);

    // Always include page and limit, with defaults
    const page = filters.page || 1;
    const limit = filters.limit || 20;
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    console.log('API request params:', params.toString());

    const response = await api.get(`/legacy-applications?${params.toString()}`);
    return response.data;
  },

  /**
   * Get application by ID
   * @param {string} id - Application ID
   * @returns {Promise<Object>} Application details
   */
  getApplicationById: async (id) => {
    const response = await api.get(`/legacy-applications/${id}`);
    return response.data;
  },

  /**
   * Get application statistics
   * @returns {Promise<Object>} Statistics by stage
   */
  getApplicationStats: async () => {
    const response = await api.get('/legacy-applications/stats');
    return response.data;
  },

  /**
   * Get filter options for applications
   * @returns {Promise<Object>} Available filter options
   */
  getFilterOptions: async () => {
    const response = await api.get('/legacy-applications/filter-options');
    return response.data;
  },

  /**
   * Get application history (audit trail)
   * @param {string} id - Application ID
   * @returns {Promise<Array>} History records
   */
  getApplicationHistory: async (id) => {
    const response = await api.get(`/legacy-applications/${id}/history`);
    return response.data;
  },

  /**
   * Advance application to next stage
   * @param {string} id - Application ID
   * @param {Object} metadata - Optional metadata
   * @returns {Promise<Object>} Updated application
   */
  advanceApplication: async (id, metadata = {}) => {
    const response = await api.patch(`/legacy-applications/${id}/advance`, { metadata });
    return response.data;
  },

  /**
   * Reject application
   * @param {string} id - Application ID
   * @param {string} reason - Rejection reason
   * @returns {Promise<Object>} Rejected application
   */
  rejectApplication: async (id, reason) => {
    const response = await api.patch(`/legacy-applications/${id}/reject`, { reason });
    return response.data;
  },

  /**
   * Withdraw application (candidate only)
   * @param {string} id - Application ID
   * @param {string} withdrawalReason - Reason for withdrawal
   * @returns {Promise<Object>} Withdrawn application
   */
  withdrawApplication: async (id, withdrawalReason) => {
    const response = await api.patch(`/legacy-applications/${id}/withdraw`, { withdrawalReason });
    return response.data;
  },

  /**
   * Upload document for application
   * @param {string} applicationId - Application ID
   * @param {File} file - File to upload
   * @param {string} documentType - Document type (PASSPORT, PHOTO, etc.)
   * @returns {Promise<Object>} Uploaded document
   */
  uploadDocument: async (applicationId, file, documentType, customDocumentName = '') => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);
    if (customDocumentName) {
      formData.append('customDocumentName', customDocumentName);
    }

    const response = await api.post(`/legacy-applications/${applicationId}/documents/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * Get all documents for application
   * @param {string} applicationId - Application ID
   * @returns {Promise<Array>} List of documents
   */
  getApplicationDocuments: async (applicationId) => {
    const response = await api.get(`/legacy-applications/${applicationId}/documents`);
    return response.data;
  },

  /**
   * Delete document from application
   * @param {string} applicationId - Application ID
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} Success response
   */
  deleteDocument: async (applicationId, documentId) => {
    const response = await api.delete(`/legacy-applications/${applicationId}/documents/${documentId}`);
    return response.data;
  },

  /**
   * Verify document (ADMIN only)
   * @param {string} applicationId - Application ID
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} Verified document
   */
  verifyDocument: async (applicationId, documentId) => {
    const response = await api.patch(`/legacy-applications/${applicationId}/documents/${documentId}/verify`);
    return response.data;
  },

  /**
   * Reject document (ADMIN only)
   * @param {string} applicationId - Application ID
   * @param {string} documentId - Document ID
   * @param {string} reason - Rejection reason
   * @returns {Promise<Object>} Rejected document
   */
  rejectDocument: async (applicationId, documentId, reason) => {
    const response = await api.patch(`/legacy-applications/${applicationId}/documents/${documentId}/reject`, { reason });
    return response.data;
  },

  /**
   * Get interviews for application
   * @param {string} applicationId - Application ID
   * @returns {Promise<Object>} Application interviews
   */
  getApplicationInterviews: async (applicationId) => {
    const response = await api.get(`/legacy-applications/${applicationId}/interview`);
    return response.data;
  },

  getAgreement: async (applicationId) => {
    const response = await api.get(`/legacy-applications/${applicationId}/agreement`);
    return response.data;
  },

  uploadAgreement: async (applicationId, payload) => {
    const formData = new FormData();
    formData.append('document', payload.file);
    formData.append('amount', payload.amount);
    if (payload.termsText) {
      formData.append('termsText', payload.termsText);
    }

    const response = await api.post(`/legacy-applications/${applicationId}/agreement/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  acceptAgreement: async (applicationId) => {
    const response = await api.post(`/legacy-applications/${applicationId}/agreement/accept`);
    return response.data;
  },

  uploadPayment: async (applicationId, payload) => {
    const formData = new FormData();
    formData.append('document', payload.file);
    formData.append('transactionId', payload.transactionId);

    const response = await api.post(`/legacy-applications/${applicationId}/payment/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  getPaymentStatus: async (applicationId) => {
    const response = await api.get(`/legacy-applications/${applicationId}/payment/status`);
    return response.data;
  },

  reviewPayment: async (applicationId, action, reason) => {
    const response = await api.post(`/legacy-applications/${applicationId}/payment/review`, { action, reason });
    return response.data;
  },

  requestPaymentRefund: async (applicationId, reason) => {
    const response = await api.post(`/legacy-applications/${applicationId}/payment/refund-request`, { reason });
    return response.data;
  },

  addEsignLink: async (applicationId, esignLink) => {
    const response = await api.post(`/legacy-applications/${applicationId}/esign/link`, { esignLink });
    return response.data;
  },

  getEsignLink: async (applicationId) => {
    const response = await api.get(`/legacy-applications/${applicationId}/esign/link`);
    return response.data;
  },

  markEsignComplete: async (applicationId) => {
    const response = await api.post(`/legacy-applications/${applicationId}/esign/complete`, {});
    return response.data;
  },

  reviewEsign: async (applicationId, action, reason) => {
    const response = await api.post(`/legacy-applications/${applicationId}/esign/review`, { action, reason });
    return response.data;
  },

  getOfferLetter: async (applicationId) => {
    const response = await api.get(`/legacy-applications/${applicationId}/offer-letter`);
    return response.data;
  },

  generateOfferLetter: async (applicationId, payload) => {
    const response = await api.post(`/legacy-applications/${applicationId}/offer-letter/generate`, payload);
    return response.data;
  },

  uploadOfferLetter: async (applicationId, file) => {
    const formData = new FormData();
    if (file) {
      formData.append('document', file);
    }

    const response = await api.post(`/legacy-applications/${applicationId}/offer-letter/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  uploadSignedOfferLetter: async (applicationId, signedFile) => {
    const formData = new FormData();
    if (signedFile) {
      formData.append('document', signedFile);
    }

    const response = await api.post(`/legacy-applications/${applicationId}/offer-letter/signed-upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  reviewSignedOfferLetter: async (applicationId, action, reason) => {
    const response = await api.post(`/legacy-applications/${applicationId}/offer-letter/signed-review`, {
      action,
      reason
    });

    return response.data;
  },

  setupOfferLetterPayment: async (applicationId, amount, payerType = 'candidate') => {
    const response = await api.post(`/legacy-applications/${applicationId}/offer-letter/payment/setup`, { amount, payerType });
    return response.data;
  },

  uploadOfferLetterPayment: async (applicationId, payload) => {
    const formData = new FormData();
    formData.append('transactionId', payload.transactionId);
    if (payload.corporateName) {
      formData.append('corporateName', payload.corporateName);
    }
    if (payload.file) {
      formData.append('document', payload.file);
    }

    const response = await api.post(`/legacy-applications/${applicationId}/offer-letter/payment/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  reviewOfferLetterPayment: async (applicationId, action, reason) => {
    const response = await api.post(`/legacy-applications/${applicationId}/offer-letter/payment/review`, { action, reason });
    return response.data;
  },

  requestOfferLetterPaymentRefund: async (applicationId, reason) => {
    const response = await api.post(`/legacy-applications/${applicationId}/offer-letter/payment/refund-request`, { reason });
    return response.data;
  },

  getTicket: async (applicationId) => {
    const response = await api.get(`/legacy-applications/${applicationId}/ticket`);
    return response.data;
  },

  proposeTicketBookingDate: async (applicationId, bookingDate, note) => {
    const response = await api.post(`/legacy-applications/${applicationId}/ticket/booking/propose`, {
      bookingDate,
      note
    });
    return response.data;
  },

  respondTicketBookingDate: async (applicationId, action, reason) => {
    const response = await api.post(`/legacy-applications/${applicationId}/ticket/booking/respond`, {
      action,
      reason
    });
    return response.data;
  },

  uploadTicket: async (applicationId, ticketFile) => {
    const formData = new FormData();
    if (ticketFile) {
      formData.append('document', ticketFile);
    }

    const response = await api.post(`/legacy-applications/${applicationId}/ticket/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  uploadPreDepartureCertificate: async (applicationId, certificateFile) => {
    const formData = new FormData();
    if (certificateFile) {
      formData.append('document', certificateFile);
    }

    const response = await api.post(`/legacy-applications/${applicationId}/ticket/pre-departure/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  uploadMedicalForm: async (applicationId, formFile) => {
    const formData = new FormData();
    if (formFile) {
      formData.append('document', formFile);
    }

    const response = await api.post(`/legacy-applications/${applicationId}/medical/form/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  applyForTPCPractising: async (applicationId) => {
    const response = await api.post(`/legacy-applications/${applicationId}/tpc/apply`);
    return response.data;
  },

  uploadTPCPractisingCertificate: async (applicationId, certificateFile) => {
    const formData = new FormData();
    if (certificateFile) {
      formData.append('document', certificateFile);
    }

    const response = await api.post(`/legacy-applications/${applicationId}/tpc/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  applyForESDApproval: async (applicationId) => {
    const response = await api.post(`/legacy-applications/${applicationId}/esd/apply`);
    return response.data;
  },

  uploadESDCertificate: async (applicationId, certificateFile) => {
    const formData = new FormData();
    if (certificateFile) {
      formData.append('document', certificateFile);
    }

    const response = await api.post(`/legacy-applications/${applicationId}/esd/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  applyForVisaProcess: async (applicationId) => {
    const response = await api.post(`/legacy-applications/${applicationId}/visa/apply`);
    return response.data;
  },

  uploadVisaForm: async (applicationId, formFile) => {
    const formData = new FormData();
    if (formFile) {
      formData.append('document', formFile);
    }

    const response = await api.post(`/legacy-applications/${applicationId}/visa/form/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  approveClearance: async (applicationId) => {
    const response = await api.post(`/legacy-applications/${applicationId}/clearance/approve`);
    return response.data;
  },

  uploadClearanceDocument: async (applicationId, clearanceFile) => {
    const formData = new FormData();
    if (clearanceFile) {
      formData.append('document', clearanceFile);
    }

    const response = await api.post(`/legacy-applications/${applicationId}/clearance/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  getDepartureSubmission: async (applicationId) => {
    const response = await api.get(`/legacy-applications/${applicationId}/departure`);
    return response.data;
  },

  submitDeparture: async (applicationId, payload) => {
    const formData = new FormData();
    formData.append('feedback', payload.feedback);
    if (payload.file) {
      formData.append('document', payload.file);
    }

    const response = await api.post(`/legacy-applications/${applicationId}/departure/submit`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  deleteDepartureVideo: async (applicationId) => {
    const response = await api.delete(`/legacy-applications/${applicationId}/departure/video`);
    return response.data;
  },

  updateDepartureHomepageStatus: async (applicationId, showOnHomepage) => {
    const response = await api.patch(`/legacy-applications/${applicationId}/departure/homepage`, {
      showOnHomepage
    });
    return response.data;
  },

  getPublicFeedbacks: async () => {
    const response = await api.get('/legacy-applications/public/feedbacks');
    return response.data;
  },

  markDeparted: async (applicationId, remark) => {
    const response = await api.post(`/legacy-applications/${applicationId}/departure/departed`, {
      remark
    });
    return response.data;
  },

  markNotDeparted: async (applicationId, remark) => {
    const response = await api.post(`/legacy-applications/${applicationId}/departure/not-departed`, {
      remark
    });
    return response.data;
  },

  rescheduleTicket: async (applicationId, data) => {
    const formData = new FormData();
    formData.append('remark', data.remark);
    if (data.file) {
      formData.append('document', data.file);
    }

    const response = await api.post(`/legacy-applications/${applicationId}/departure/reschedule-ticket`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  }
};

export default applicationService;
